import { useState, useMemo } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { useToast } from "@/hooks/use-toast";
import { Building2, Paintbrush, LayoutGrid, Layers, Home, Leaf, Copy } from "lucide-react";
import { InfoTip } from "@/components/ui/info-tip";

// ─── Shared helpers ──────────────────────────────────────────────────────────

function n(s: string): number {
  const v = parseFloat(s);
  return isFinite(v) ? v : 0;
}

function round(v: number, places = 2): number {
  const f = 10 ** places;
  return Math.round(v * f) / f;
}

function NumField({
  label, value, onChange, unit, placeholder = "0", min = 0,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  unit?: string;
  placeholder?: string;
  min?: number;
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flex: 1, minWidth: 0 }}>
      <label style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "hsl(var(--muted-foreground))" }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <Input
          type="number" inputMode="decimal" min={min} value={value} placeholder={placeholder}
          onChange={(e) => onChange(e.target.value)}
          style={{ height: "2.75rem", fontSize: "1rem", fontWeight: 600, textAlign: "left", paddingRight: unit ? "2.5rem" : undefined }}
        />
        {unit && (
          <span style={{ position: "absolute", right: "0.6rem", top: "50%", transform: "translateY(-50%)", fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", pointerEvents: "none" }}>
            {unit}
          </span>
        )}
      </div>
    </div>
  );
}

function FieldRow({ children }: { children: React.ReactNode }) {
  return <div style={{ display: "flex", gap: "0.5rem", alignItems: "flex-end" }}>{children}</div>;
}

function SelectField({
  label, value, onChange, options,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
}) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "0.3rem", flex: 1, minWidth: 0 }}>
      <label style={{ fontSize: "0.7rem", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: "hsl(var(--muted-foreground))" }}>
        {label}
      </label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger style={{ height: "2.75rem", fontSize: "0.9375rem" }}>
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((o) => (
            <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}

function ResultCard({
  primary, rows, onCopy, formula,
}: {
  primary: { value: string; label: string };
  rows: { label: string; value: string }[];
  onCopy: () => void;
  formula?: string;
}) {
  return (
    <div style={{ border: "1px solid hsl(var(--border))", borderRadius: "0.625rem", overflow: "hidden" }}>
      <div style={{ backgroundColor: "hsl(var(--primary))", color: "hsl(var(--primary-foreground))", padding: "0.875rem 1rem", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <div style={{ fontSize: "1.625rem", fontWeight: 700, lineHeight: 1.1, fontVariantNumeric: "tabular-nums" }}>{primary.value}</div>
          <div style={{ fontSize: "0.75rem", fontWeight: 600, opacity: 0.9, marginTop: "0.15rem", textTransform: "uppercase", letterSpacing: "0.03em", display: "flex", alignItems: "center", gap: "0.3rem" }}>
            {primary.label}
            {formula && <InfoTip content={formula} className="text-primary-foreground/70 hover:text-primary-foreground" />}
          </div>
        </div>
        <button onClick={onCopy} aria-label="Copy result" style={{ display: "flex", alignItems: "center", justifyContent: "center", width: "2.25rem", height: "2.25rem", borderRadius: "0.4rem", border: "none", backgroundColor: "rgba(255,255,255,0.18)", color: "inherit", cursor: "pointer", flexShrink: 0 }}>
          <Copy style={{ width: "1rem", height: "1rem" }} />
        </button>
      </div>
      {rows.length > 0 && (
        <div style={{ padding: "0.25rem 1rem 0.5rem" }}>
          {rows.map((r, i) => (
            <div key={i} style={{ display: "flex", justifyContent: "space-between", padding: "0.4rem 0", borderBottom: i < rows.length - 1 ? "1px solid hsl(var(--border))" : "none", fontSize: "0.875rem" }}>
              <span style={{ color: "hsl(var(--muted-foreground))" }}>{r.label}</span>
              <span style={{ fontWeight: 600, fontVariantNumeric: "tabular-nums" }}>{r.value}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function Note({ children }: { children: React.ReactNode }) {
  return <p style={{ fontSize: "0.75rem", color: "hsl(var(--muted-foreground))", fontStyle: "italic", lineHeight: 1.5, margin: 0 }}>{children}</p>;
}

function SubPicker({
  options, value, onChange,
}: {
  options: { id: string; label: string }[];
  value: string;
  onChange: (v: string) => void;
}) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: `repeat(${options.length}, 1fr)`, gap: "0.375rem" }}>
      {options.map((o) => {
        const on = value === o.id;
        return (
          <button
            key={o.id}
            onClick={() => onChange(o.id)}
            style={{
              padding: "0.5rem 0.25rem", borderRadius: "0.375rem", cursor: "pointer",
              fontSize: "0.8125rem", fontWeight: 600,
              border: "1px solid " + (on ? "hsl(var(--primary))" : "hsl(var(--border))"),
              backgroundColor: on ? "hsl(var(--primary))" : "transparent",
              color: on ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))",
            }}
          >
            {o.label}
          </button>
        );
      })}
    </div>
  );
}

const SECTION_GAP: React.CSSProperties = { display: "flex", flexDirection: "column", gap: "0.875rem" };

// ─── Concrete ─────────────────────────────────────────────────────────────────

const BAG_YIELD: Record<string, number> = { "40": 0.3, "60": 0.45, "80": 0.6 };

function ConcreteCalc() {
  const { toast } = useToast();
  const [shape, setShape] = useState("slab");
  const [bag, setBag] = useState("80");
  const [waste, setWaste] = useState("10");

  // slab
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [thick, setThick] = useState("4");
  // footing
  const [fLen, setFLen] = useState("");
  const [fWid, setFWid] = useState("8");
  const [fDepth, setFDepth] = useState("12");
  const [fQty, setFQty] = useState("1");
  // column
  const [dia, setDia] = useState("");
  const [hgt, setHgt] = useState("");
  const [cQty, setCQty] = useState("1");

  const result = useMemo(() => {
    let ft3 = 0;
    if (shape === "slab") ft3 = n(len) * n(wid) * (n(thick) / 12);
    else if (shape === "footing") ft3 = n(fLen) * (n(fWid) / 12) * (n(fDepth) / 12) * Math.max(1, n(fQty));
    else if (shape === "column") ft3 = Math.PI * (n(dia) / 12 / 2) ** 2 * n(hgt) * Math.max(1, n(cQty));

    const withWaste = ft3 * (1 + n(waste) / 100);
    const yd3 = withWaste / 27;
    const bags = bag === "ready" ? 0 : Math.ceil(withWaste / BAG_YIELD[bag]);
    return { ft3, withWaste, yd3, bags };
  }, [shape, bag, waste, len, wid, thick, fLen, fWid, fDepth, fQty, dia, hgt, cQty]);

  const primary = bag === "ready"
    ? { value: `${round(Math.ceil(result.yd3 * 4) / 4, 2)} yd³`, label: "Ready-mix (incl. waste)" }
    : { value: `${result.bags} bags`, label: `${bag} lb bags (incl. waste)` };

  const rows = [
    { label: "Net volume", value: `${round(result.ft3)} ft³` },
    { label: "With waste", value: `${round(result.withWaste)} ft³` },
    { label: "Cubic yards", value: `${round(result.yd3)} yd³` },
  ];

  const copy = () => copyResult(toast, "Concrete", primary, rows);

  return (
    <div style={SECTION_GAP}>
      <FieldRow>
        <SelectField label="Shape" value={shape} onChange={setShape} options={[
          { value: "slab", label: "Slab" },
          { value: "footing", label: "Footing" },
          { value: "column", label: "Round Column" },
        ]} />
      </FieldRow>

      {shape === "slab" && (
        <FieldRow>
          <NumField label="Length" value={len} onChange={setLen} unit="ft" />
          <NumField label="Width" value={wid} onChange={setWid} unit="ft" />
          <NumField label="Thickness" value={thick} onChange={setThick} unit="in" />
        </FieldRow>
      )}
      {shape === "footing" && (
        <>
          <FieldRow>
            <NumField label="Length" value={fLen} onChange={setFLen} unit="ft" />
            <NumField label="Width" value={fWid} onChange={setFWid} unit="in" />
          </FieldRow>
          <FieldRow>
            <NumField label="Depth" value={fDepth} onChange={setFDepth} unit="in" />
            <NumField label="Quantity" value={fQty} onChange={setFQty} min={1} />
          </FieldRow>
        </>
      )}
      {shape === "column" && (
        <FieldRow>
          <NumField label="Diameter" value={dia} onChange={setDia} unit="in" />
          <NumField label="Height" value={hgt} onChange={setHgt} unit="ft" />
          <NumField label="Quantity" value={cQty} onChange={setCQty} min={1} />
        </FieldRow>
      )}

      <FieldRow>
        <SelectField label="Order As" value={bag} onChange={setBag} options={[
          { value: "40", label: "40 lb bags" },
          { value: "60", label: "60 lb bags" },
          { value: "80", label: "80 lb bags" },
          { value: "ready", label: "Ready-mix (yd³)" },
        ]} />
        <NumField label="Waste %" value={waste} onChange={setWaste} unit="%" />
      </FieldRow>

      <ResultCard primary={primary} rows={rows} onCopy={copy} formula="Volume × (1 + waste %), then ÷ bag yield for bag count (or ÷ 27 for cubic yards of ready-mix), rounded up." />
      <Note>Bag yields: 40 lb ≈ 0.30 ft³, 60 lb ≈ 0.45 ft³, 80 lb ≈ 0.60 ft³. Order a little extra for spillage and uneven sub-base.</Note>
    </div>
  );
}

// ─── Paint ────────────────────────────────────────────────────────────────────

const PAINT_COVERAGE: Record<string, { label: string; cov: number }> = {
  flat: { label: "Flat / Matte (375)", cov: 375 },
  satin: { label: "Eggshell / Satin (325)", cov: 325 },
  gloss: { label: "Semi-gloss / Gloss (275)", cov: 275 },
  primer: { label: "Primer (325)", cov: 325 },
  exterior: { label: "Exterior (275)", cov: 275 },
};

function PaintCalc() {
  const { toast } = useToast();
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [hgt, setHgt] = useState("8");
  const [ceiling, setCeiling] = useState("no");
  const [doors, setDoors] = useState("0");
  const [windows, setWindows] = useState("0");
  const [coats, setCoats] = useState("2");
  const [finish, setFinish] = useState("satin");
  const [waste, setWaste] = useState("10");

  const result = useMemo(() => {
    const L = n(len), W = n(wid), H = n(hgt);
    const wallArea = 2 * (L + W) * H;
    const ceilingArea = ceiling === "yes" ? L * W : 0;
    const gross = wallArea + ceilingArea;
    const deductions = n(doors) * 21 + n(windows) * 15;
    const net = Math.max(0, gross - deductions);
    const cov = PAINT_COVERAGE[finish].cov;
    const needed = net * Math.max(1, n(coats)) * (1 + n(waste) / 100);
    const gallons = cov > 0 ? Math.ceil(needed / cov) : 0;
    return { gross, net, gallons };
  }, [len, wid, hgt, ceiling, doors, windows, coats, finish, waste]);

  const primary = { value: `${result.gallons} gal`, label: `${PAINT_COVERAGE[finish].label.split(" (")[0]} · ${coats} coat${n(coats) === 1 ? "" : "s"}` };
  const rows = [
    { label: "Gross area", value: `${round(result.gross, 0)} sq ft` },
    { label: "Paintable area", value: `${round(result.net, 0)} sq ft` },
    { label: "≈ Quarts", value: `${result.gallons * 4} qt` },
  ];
  const copy = () => copyResult(toast, "Paint", primary, rows);

  return (
    <div style={SECTION_GAP}>
      <FieldRow>
        <NumField label="Room Length" value={len} onChange={setLen} unit="ft" />
        <NumField label="Room Width" value={wid} onChange={setWid} unit="ft" />
        <NumField label="Ceiling Ht" value={hgt} onChange={setHgt} unit="ft" />
      </FieldRow>
      <FieldRow>
        <NumField label="Doors" value={doors} onChange={setDoors} />
        <NumField label="Windows" value={windows} onChange={setWindows} />
        <SelectField label="Paint Ceiling" value={ceiling} onChange={setCeiling} options={[
          { value: "no", label: "No" }, { value: "yes", label: "Yes" },
        ]} />
      </FieldRow>
      <FieldRow>
        <SelectField label="Finish" value={finish} onChange={setFinish} options={Object.entries(PAINT_COVERAGE).map(([v, o]) => ({ value: v, label: o.label }))} />
      </FieldRow>
      <FieldRow>
        <NumField label="Coats" value={coats} onChange={setCoats} min={1} />
        <NumField label="Waste %" value={waste} onChange={setWaste} unit="%" />
      </FieldRow>

      <ResultCard primary={primary} rows={rows} onCopy={copy} formula="(Paintable area × coats × (1 + waste %)) ÷ coverage per gallon, rounded up. Paintable area = walls + ceiling − door/window deductions." />
      <Note>Deducts 21 sq ft per door and 15 sq ft per window. Coverage per gallon shown in parentheses — adjust waste for heavy texture or color changes.</Note>
    </div>
  );
}

// ─── Flooring ─────────────────────────────────────────────────────────────────

const FLOOR_WASTE: Record<string, { label: string; pct: number }> = {
  hw_straight: { label: "Hardwood — straight", pct: 10 },
  hw_diag: { label: "Hardwood — diagonal", pct: 14 },
  lvp_straight: { label: "Laminate / LVP — straight", pct: 7 },
  lvp_diag: { label: "Laminate / LVP — diagonal", pct: 10 },
  tile_straight: { label: "Tile — straight", pct: 10 },
  tile_diag: { label: "Tile — diagonal", pct: 15 },
  tile_complex: { label: "Tile — complex", pct: 18 },
  carpet: { label: "Carpet / sheet vinyl", pct: 12 },
};

function FlooringCalc() {
  const { toast } = useToast();
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [material, setMaterial] = useState("lvp_straight");
  const [waste, setWaste] = useState("7");
  const [box, setBox] = useState("20");

  const onMaterial = (v: string) => {
    setMaterial(v);
    setWaste(String(FLOOR_WASTE[v].pct));
  };

  const result = useMemo(() => {
    const area = n(len) * n(wid);
    const withWaste = area * (1 + n(waste) / 100);
    const boxes = n(box) > 0 ? Math.ceil(withWaste / n(box)) : 0;
    return { area, withWaste, boxes };
  }, [len, wid, waste, box]);

  const primary = { value: `${result.boxes} boxes`, label: `Order @ ${n(box) || 0} sq ft/box` };
  const rows = [
    { label: "Floor area", value: `${round(result.area, 0)} sq ft` },
    { label: `With ${round(n(waste), 0)}% waste`, value: `${round(result.withWaste, 0)} sq ft` },
    { label: "Underlayment", value: `${round(result.area, 0)} sq ft` },
  ];
  const copy = () => copyResult(toast, "Flooring", primary, rows);

  return (
    <div style={SECTION_GAP}>
      <FieldRow>
        <NumField label="Room Length" value={len} onChange={setLen} unit="ft" />
        <NumField label="Room Width" value={wid} onChange={setWid} unit="ft" />
      </FieldRow>
      <FieldRow>
        <SelectField label="Material" value={material} onChange={onMaterial} options={Object.entries(FLOOR_WASTE).map(([v, o]) => ({ value: v, label: o.label }))} />
      </FieldRow>
      <FieldRow>
        <NumField label="Waste %" value={waste} onChange={setWaste} unit="%" />
        <NumField label="Box / Carton" value={box} onChange={setBox} unit="sq ft" />
      </FieldRow>

      <ResultCard primary={primary} rows={rows} onCopy={copy} formula="Floor area × (1 + waste %) ÷ sq ft per box, rounded up to whole boxes." />
      <Note>Selecting a material presets a typical waste factor — override it for odd-shaped rooms or large-format tile. Buy from the same dye/run lot.</Note>
    </div>
  );
}

// ─── Drywall ──────────────────────────────────────────────────────────────────

const SHEET_COVERAGE: Record<string, { label: string; cov: number }> = {
  "48": { label: "4×12 (48 sq ft)", cov: 48 },
  "40": { label: "4×10 (40 sq ft)", cov: 40 },
  "32": { label: "4×8 (32 sq ft)", cov: 32 },
};

function DrywallCalc() {
  const { toast } = useToast();
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [hgt, setHgt] = useState("8");
  const [ceiling, setCeiling] = useState("yes");
  const [sheet, setSheet] = useState("32");
  const [waste, setWaste] = useState("12");

  const result = useMemo(() => {
    const L = n(len), W = n(wid), H = n(hgt);
    const wallArea = 2 * (L + W) * H;
    const ceilingArea = ceiling === "yes" ? L * W : 0;
    const gross = wallArea + ceilingArea;
    const withWaste = gross * (1 + n(waste) / 100);
    const sheets = Math.ceil(withWaste / SHEET_COVERAGE[sheet].cov);
    const screwsLb = Math.ceil(gross / 200);
    const mudGal = Math.ceil(gross / 100);
    const tapeRolls = Math.ceil(gross / 550);
    return { gross, sheets, screwsLb, mudGal, tapeRolls };
  }, [len, wid, hgt, ceiling, sheet, waste]);

  const primary = { value: `${result.sheets} sheets`, label: `${SHEET_COVERAGE[sheet].label.split(" (")[0]} (incl. waste)` };
  const rows = [
    { label: "Total area", value: `${round(result.gross, 0)} sq ft` },
    { label: "Screws", value: `${result.screwsLb} lb` },
    { label: "Joint compound", value: `${result.mudGal} gal` },
    { label: "Tape", value: `${result.tapeRolls} roll${result.tapeRolls === 1 ? "" : "s"}` },
  ];
  const copy = () => copyResult(toast, "Drywall", primary, rows);

  return (
    <div style={SECTION_GAP}>
      <FieldRow>
        <NumField label="Room Length" value={len} onChange={setLen} unit="ft" />
        <NumField label="Room Width" value={wid} onChange={setWid} unit="ft" />
        <NumField label="Wall Ht" value={hgt} onChange={setHgt} unit="ft" />
      </FieldRow>
      <FieldRow>
        <SelectField label="Sheet Size" value={sheet} onChange={setSheet} options={Object.entries(SHEET_COVERAGE).map(([v, o]) => ({ value: v, label: o.label }))} />
      </FieldRow>
      <FieldRow>
        <SelectField label="Cover Ceiling" value={ceiling} onChange={setCeiling} options={[
          { value: "yes", label: "Yes" }, { value: "no", label: "No" },
        ]} />
        <NumField label="Waste %" value={waste} onChange={setWaste} unit="%" />
      </FieldRow>

      <ResultCard primary={primary} rows={rows} onCopy={copy} formula="(Wall + ceiling area) × (1 + waste %) ÷ sheet coverage, rounded up to whole sheets." />
      <Note>Consumables (≈1 lb screws / 200 sq ft, 1 gal mud / 100 sq ft, 1 roll tape / 550 sq ft) are based on a standard 3-coat finish.</Note>
    </div>
  );
}

// ─── Roofing ──────────────────────────────────────────────────────────────────

const PITCH_FACTOR: Record<string, { label: string; factor: number }> = {
  flat:   { label: "Flat / Low ≤2:12", factor: 1.02 },
  p3_12:  { label: "3:12",             factor: 1.04 },
  p4_12:  { label: "4:12",             factor: 1.06 },
  p5_12:  { label: "5:12",             factor: 1.08 },
  p6_12:  { label: "6:12",             factor: 1.12 },
  p7_12:  { label: "7:12",             factor: 1.16 },
  p8_12:  { label: "8:12",             factor: 1.20 },
  p9_12:  { label: "9:12",             factor: 1.25 },
  p10_12: { label: "10:12",            factor: 1.30 },
  p12_12: { label: "12:12 (steep)",    factor: 1.41 },
};

const UNDERLAYMENT: Record<string, { label: string; sqPerRoll: number }> = {
  synthetic: { label: "Synthetic (10 sq/roll)", sqPerRoll: 10 },
  felt15:    { label: "15-lb felt (4 sq/roll)",  sqPerRoll: 4  },
  felt30:    { label: "30-lb felt (2 sq/roll)",  sqPerRoll: 2  },
};

function RoofingCalc() {
  const { toast } = useToast();
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [pitch, setPitch] = useState("p4_12");
  const [under, setUnder] = useState("synthetic");
  const [waste, setWaste] = useState("10");

  const result = useMemo(() => {
    const footprint = n(len) * n(wid);
    const slopedArea = footprint * PITCH_FACTOR[pitch].factor;
    const squares = slopedArea / 100;
    const squaresWithWaste = squares * (1 + n(waste) / 100);
    const bundles = Math.ceil(squaresWithWaste * 3); // 3 bundles = 1 square
    const sqPerRoll = UNDERLAYMENT[under].sqPerRoll;
    const underlayRolls = Math.ceil(squaresWithWaste / sqPerRoll);
    const nailsLb = Math.ceil(squaresWithWaste * 1.75); // ~1.75 lb/sq for nails
    return { footprint, slopedArea, squares, squaresWithWaste, bundles, underlayRolls, nailsLb };
  }, [len, wid, pitch, under, waste]);

  const primary = { value: `${result.bundles} bundles`, label: "Shingles (incl. waste)" };
  const rows = [
    { label: "Footprint area", value: `${round(result.footprint, 0)} sq ft` },
    { label: "Sloped area", value: `${round(result.slopedArea, 0)} sq ft` },
    { label: "Squares", value: `${round(result.squaresWithWaste, 2)} sq` },
    { label: "Underlayment", value: `${result.underlayRolls} roll${result.underlayRolls === 1 ? "" : "s"}` },
    { label: "Nails (est.)", value: `${result.nailsLb} lb` },
  ];

  const copy = () => copyResult(toast, "Roofing", primary, rows);

  return (
    <div style={SECTION_GAP}>
      <FieldRow>
        <NumField label="Footprint Length" value={len} onChange={setLen} unit="ft" />
        <NumField label="Footprint Width" value={wid} onChange={setWid} unit="ft" />
      </FieldRow>
      <FieldRow>
        <SelectField label="Roof Pitch" value={pitch} onChange={setPitch}
          options={Object.entries(PITCH_FACTOR).map(([v, o]) => ({ value: v, label: o.label }))} />
        <NumField label="Waste %" value={waste} onChange={setWaste} unit="%" />
      </FieldRow>
      <FieldRow>
        <SelectField label="Underlayment" value={under} onChange={setUnder}
          options={Object.entries(UNDERLAYMENT).map(([v, o]) => ({ value: v, label: o.label }))} />
      </FieldRow>

      <ResultCard primary={primary} rows={rows} onCopy={copy} formula="Footprint × pitch factor = sloped area, × (1 + waste %), ÷ 100 = squares; × 3 = bundles, rounded up." />
      <Note>Footprint × pitch factor = sloped area. 1 square = 100 sq ft; 3 bundles = 1 square (standard 3-tab or architectural). Add extra bundles for ridge cap and hip/valley cuts.</Note>
    </div>
  );
}

// ─── Landscaping ─────────────────────────────────────────────────────────────

const BULK_MATERIALS: Record<string, { label: string; tonsPerYd3?: number }> = {
  mulch:   { label: "Mulch (wood chip / bark)" },
  gravel:  { label: "Gravel / Crushed stone",  tonsPerYd3: 1.5  },
  pea:     { label: "Pea gravel / River rock",  tonsPerYd3: 1.35 },
  sand:    { label: "Sand",                     tonsPerYd3: 1.3  },
  topsoil: { label: "Topsoil / Compost" },
};

function LandscapingCalc() {
  const { toast } = useToast();
  const [mode, setMode] = useState("bulk"); // "bulk" | "sod"

  // Bulk / bagged (mulch, gravel, topsoil)
  const [len, setLen] = useState("");
  const [wid, setWid] = useState("");
  const [depth, setDepth] = useState("3");
  const [material, setMaterial] = useState("mulch");
  const [bagSize, setBagSize] = useState("2"); // cu ft per bag

  // Sod
  const [sodLen, setSodLen] = useState("");
  const [sodWid, setSodWid] = useState("");
  const [sodWaste, setSodWaste] = useState("5");

  const bulkResult = useMemo(() => {
    const area = n(len) * n(wid);
    const cuFt = area * (n(depth) / 12);
    const cuYd = cuFt / 27;
    const bags = n(bagSize) > 0 ? Math.ceil(cuFt / n(bagSize)) : 0;
    const mat = BULK_MATERIALS[material];
    const tons = mat.tonsPerYd3 != null ? round(cuYd * mat.tonsPerYd3, 2) : null;
    return { area, cuFt, cuYd, bags, tons };
  }, [len, wid, depth, material, bagSize]);

  const sodResult = useMemo(() => {
    const area = n(sodLen) * n(sodWid);
    const withWaste = area * (1 + n(sodWaste) / 100);
    const rolls = Math.ceil(withWaste / 9);     // ~9 sq ft/roll
    const pallets = Math.ceil(withWaste / 450); // ~450 sq ft/pallet
    return { area, withWaste, rolls, pallets };
  }, [sodLen, sodWid, sodWaste]);

  let primary: { value: string; label: string };
  let rows: { label: string; value: string }[];
  let copyTitle: string;

  if (mode === "sod") {
    primary = { value: `${sodResult.rolls} rolls`, label: "Sod @ ~9 sq ft/roll (incl. waste)" };
    rows = [
      { label: "Lawn area", value: `${round(sodResult.area, 0)} sq ft` },
      { label: `With ${n(sodWaste)}% waste`, value: `${round(sodResult.withWaste, 0)} sq ft` },
      { label: "Pallets (~450 sq ft)", value: `${sodResult.pallets} pallet${sodResult.pallets === 1 ? "" : "s"}` },
    ];
    copyTitle = "Sod";
  } else {
    primary = { value: `${bulkResult.bags} bags`, label: `${n(bagSize)} cu ft bags` };
    rows = [
      { label: "Coverage area", value: `${round(bulkResult.area, 0)} sq ft` },
      { label: "Cubic feet", value: `${round(bulkResult.cuFt, 2)} ft³` },
      { label: "Cubic yards", value: `${round(bulkResult.cuYd, 2)} yd³` },
      ...(bulkResult.tons != null ? [{ label: "Weight (est.)", value: `${bulkResult.tons} tons` }] : []),
    ];
    copyTitle = BULK_MATERIALS[material].label.split(" (")[0].split(" /")[0];
  }

  const copy = () => copyResult(toast, copyTitle, primary, rows);

  return (
    <div style={SECTION_GAP}>
      <SubPicker
        options={[{ id: "bulk", label: "Mulch / Fill" }, { id: "sod", label: "Sod" }]}
        value={mode}
        onChange={setMode}
      />

      {mode === "bulk" && (
        <>
          <FieldRow>
            <NumField label="Length" value={len} onChange={setLen} unit="ft" />
            <NumField label="Width" value={wid} onChange={setWid} unit="ft" />
            <NumField label="Depth" value={depth} onChange={setDepth} unit="in" />
          </FieldRow>
          <FieldRow>
            <SelectField label="Material" value={material} onChange={setMaterial}
              options={Object.entries(BULK_MATERIALS).map(([v, o]) => ({ value: v, label: o.label }))} />
          </FieldRow>
          <FieldRow>
            <NumField label="Bag Size" value={bagSize} onChange={setBagSize} unit="cu ft" />
          </FieldRow>
          <ResultCard primary={primary} rows={rows} onCopy={copy} formula="Area × depth = volume, then ÷ bag size for bags (rounded up) or ÷ 27 for cubic yards. No waste is added, so allow 10–15% extra for settling." />
          <Note>Standard bags are 2 cu ft; bulk is ordered by the cubic yard. Gravel/sand weight shown for ordering by the ton — confirm density with your supplier.</Note>
        </>
      )}

      {mode === "sod" && (
        <>
          <FieldRow>
            <NumField label="Length" value={sodLen} onChange={setSodLen} unit="ft" />
            <NumField label="Width" value={sodWid} onChange={setSodWid} unit="ft" />
            <NumField label="Waste %" value={sodWaste} onChange={setSodWaste} unit="%" />
          </FieldRow>
          <ResultCard primary={primary} rows={rows} onCopy={copy} formula="Area × (1 + waste %) ÷ ~9 sq ft per roll, rounded up to whole rolls." />
          <Note>~9 sq ft per roll; pallets vary by supplier (400–500 sq ft — confirm before ordering). Add 5–10% for cuts and irregular edges.</Note>
        </>
      )}
    </div>
  );
}

// ─── Copy helper ──────────────────────────────────────────────────────────────

function copyResult(
  toast: ReturnType<typeof useToast>["toast"],
  title: string,
  primary: { value: string; label: string },
  rows: { label: string; value: string }[],
) {
  const text = [
    `${title}`,
    `${primary.value} — ${primary.label}`,
    ...rows.map((r) => `${r.label}: ${r.value}`),
  ].join("\n");
  navigator.clipboard.writeText(text).then(
    () => toast({ title: `${title} estimate copied` }),
    () => toast({ title: "Couldn't copy", variant: "destructive" }),
  );
}

// ─── Top-level: material picker ───────────────────────────────────────────────

const CALCS = [
  { id: "concrete",    label: "Concrete",    icon: Building2, Comp: ConcreteCalc    },
  { id: "paint",       label: "Paint",       icon: Paintbrush, Comp: PaintCalc      },
  { id: "flooring",    label: "Flooring",    icon: LayoutGrid, Comp: FlooringCalc   },
  { id: "drywall",     label: "Drywall",     icon: Layers,     Comp: DrywallCalc    },
  { id: "roofing",     label: "Roofing",     icon: Home,       Comp: RoofingCalc    },
  { id: "landscaping", label: "Landscaping", icon: Leaf,       Comp: LandscapingCalc },
] as const;

export default function MaterialCalculators() {
  const [active, setActive] = useState<(typeof CALCS)[number]["id"]>("concrete");
  const Active = CALCS.find((c) => c.id === active)!.Comp;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
      {/* 3×2 grid — fits all 6 calculators cleanly */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: "0.375rem" }}>
        {CALCS.map((c) => {
          const Icon = c.icon;
          const on = active === c.id;
          return (
            <button
              key={c.id}
              onClick={() => setActive(c.id)}
              style={{
                display: "flex", flexDirection: "column", alignItems: "center", gap: "0.25rem",
                padding: "0.625rem 0.25rem", borderRadius: "0.5rem", cursor: "pointer",
                border: "1px solid " + (on ? "hsl(var(--primary))" : "hsl(var(--border))"),
                backgroundColor: on ? "hsl(var(--primary))" : "transparent",
                color: on ? "hsl(var(--primary-foreground))" : "hsl(var(--muted-foreground))",
              }}
              className={on ? "" : "hover:bg-accent transition-colors"}
            >
              <Icon style={{ width: "1.125rem", height: "1.125rem" }} />
              <span style={{ fontSize: "0.6875rem", fontWeight: 600 }}>{c.label}</span>
            </button>
          );
        })}
      </div>

      <Active />
    </div>
  );
}
